import { api } from "@/api";
import type { AccountSettings, ChangePasswordRequest } from "./types";

/**
 * The BFF returns mutations as HTTP 200 with a { success, error } envelope so the
 * message survives the shared axios error interceptor, which discards response bodies.
 */
type Result = { success: boolean; error: string | null };
type AuthUrlResult = Result & { authUrl: string };

const unwrap = <T extends Result>(result: T): T => {
  if (!result.success) {
    throw new Error(result.error ?? "Something went wrong. Please try again.");
  }
  return result;
};

export const getAccountSettings = async (): Promise<AccountSettings> => {
  const response = await api.get<AccountSettings>("/account-settings");
  if (response.status !== 200) {
    throw new Error("Failed to load account settings.");
  }
  return response.data;
};

export const changePassword = async (request: ChangePasswordRequest): Promise<void> => {
  const response = await api.post<Result>("/account-settings/password", request);
  unwrap(response.data);
};

export const startSsoConnect = async (): Promise<string> => {
  const response = await api.post<AuthUrlResult>("/account-settings/sso/connect");
  return unwrap(response.data).authUrl;
};

export const disconnectSso = async (): Promise<void> => {
  const response = await api.delete<Result>("/account-settings/sso");
  unwrap(response.data);
};

export const startCalendarConnect = async (): Promise<string> => {
  const response = await api.post<AuthUrlResult>("/account-settings/microsoft-calendar/connect");
  return unwrap(response.data).authUrl;
};

export const disconnectCalendar = async (): Promise<void> => {
  const response = await api.delete<Result>("/account-settings/microsoft-calendar");
  unwrap(response.data);
};

export const startMailConnect = async (): Promise<string> => {
  const response = await api.post<AuthUrlResult>("/account-settings/microsoft-mail/connect");
  return unwrap(response.data).authUrl;
};

export const disconnectMail = async (): Promise<void> => {
  const response = await api.delete<Result>("/account-settings/microsoft-mail");
  unwrap(response.data);
};
